import logger from '../utils/logger';
import React, { useState, useEffect, useContext, useRef } from 'react';
import '../style/Results.css';
import '../style/PDFResultsModal.css';
import '../style/Labs.css';
import { capitalize, formatAppointmentDate, formatAppointmentTimeRange, uploadToBucket, convertUTCToCentral, formatDateOfBirth, dateOfBirth, formatTo12Hour, getUserId, describeFaxTiming } from '../utils/utils';
import { GlobalStateContext } from '../utils/GlobalStateContext';
import PageTopBar from './customComponents/PageTopBar';
import { Home } from 'lucide-react';
import PDFResultsViewer from './PDFResultsViewer';
import LabResultsWorkflow from './customComponents/LabResultsWorkflow';
import AbnormalFlag from './customComponents/AbnormalFlag';
import ResultRowsEditor, { EMPTY_ROW, isCompleteRow } from './customComponents/ResultRowsEditor';
import { printLabTemplate } from '../utils/printLabTemplate';
import { InputMask } from '@react-input/mask';
import GearsSpinner from './customComponents/GearsSpinner';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';


const Results = () => {

  const [labData, setLabData] = useState([]);
  const [radData, setRadData] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const {currentPatient, setCurrentPatient} = useContext(GlobalStateContext);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [results, setResults] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [viewerModal, setViewerModal] = useState({ show: false, orderData: null, orderType: null });
  const [fetchingReports, setFetchingReports] = useState(false);
  const [patientMrn, setPatientMrn] = useState(null);

  // Referred lab results (referral orders with uploaded reports)
  const [referredLabData, setReferredLabData] = useState([]);
  const [referredLabFilter, setReferredLabFilter] = useState('uploaded');
  const [referralModal, setReferralModal] = useState({ show: false, referral: null });
  const [referralPdfUrl, setReferralPdfUrl] = useState(null);
  const [referralPdfLoading, setReferralPdfLoading] = useState(false);
  const [referralValidationModal, setReferralValidationModal] = useState(false);
  const [referralValidationLoading, setReferralValidationLoading] = useState(false);
  const [referralValidationResult, setReferralValidationResult] = useState(null);
  const [referralValidationError, setReferralValidationError] = useState(null);
  const [referralFaxModal, setReferralFaxModal] = useState(false);
  const [referralFaxNumber, setReferralFaxNumber] = useState('');
  const [referralFaxSending, setReferralFaxSending] = useState(false);
  const [referralUnvalidatedFaxConfirm, setReferralUnvalidatedFaxConfirm] = useState(false);
  const [selectedReferralForPrint, setSelectedReferralForPrint] = useState('');
  // Radiology report fax (manual send / retry)
  const [radFaxOrder, setRadFaxOrder] = useState(null); // the radiology order being faxed, or null
  const [radFaxNumber, setRadFaxNumber] = useState('');
  const [radFaxSending, setRadFaxSending] = useState(false);
  // Results entry state
  // Structured analyte rows — posted as result_rows so a referral report matches the
  // analyzer report and its values run through the same abnormal evaluation.
  const [referralResultRows, setReferralResultRows] = useState([{ ...EMPTY_ROW }]);
  const [referralGenerating, setReferralGenerating] = useState(false);
  // Re-opens the entry form over a referral that already has a report on file, so a
  // wrong or superseded report can be replaced without losing the one on file first.
  const [referralReplacing, setReferralReplacing] = useState(false);
  const [referralModalLoading, setReferralModalLoading] = useState(false);
  const [referralItems, setReferralItems] = useState([]);
  const [referralRanges, setReferralRanges] = useState({});
  const [referralCheckedRanges, setReferralCheckedRanges] = useState({});
  const [dosOverride, setDosOverride] = useState('');
  // Regular lab order results template + AI validation
  const [labResultsModal, setLabResultsModal] = useState({ show: false, order: null });
  const fileInputRef = useRef();
  const BASE_URL = process.env.REACT_APP_API_URL;
  const {currentUser} = useContext(GlobalStateContext);
  const user = getUserId(currentUser);
  const type = currentUser.type;

const handleOrderSelect = (e) => {
  setSelectedOrderId(e.target.value);
};

        const fetchLabResults = async () => {
          try {
              const response = await fetch(`${BASE_URL}/orders/labResults?ris_patients_id=${currentPatient.ris_patients_id}&user=${user}&type=${currentUser.type}`);
              if (response.ok) {
              const data = await response.json();
              // logger.log("Radiology checklist: " + JSON.stringify(data));
              setLabData(data);
              }
          } catch (error) {
              logger.error("Error fetching radiology orders", error);
          }
        }
        const fetchRadResults = async () => {
          try {
              const response = await fetch(`${BASE_URL}/orders/radResults?ris_patients_id=${currentPatient.ris_patients_id}&user=${user}&type=${currentUser.type}`);
              if (response.ok) {
              const data = await response.json();
              logger.log(data)
              // logger.log("Radiology checklist: " + JSON.stringify(data));
              setRadData(data);
              }
          } catch (error) {
              logger.error("Error fetching radiology orders", error);
          }
        }

        const fetchReferredLabResults = async (filter = referredLabFilter) => {
          try {
            let url = `${BASE_URL}/labs/referrals?ris_patients_id=${currentPatient.ris_patients_id}`;
            if (filter === 'validated') url += '&validated=true';
            const response = await fetch(url);
            if (response.ok) {
              let data = await response.json();
              if (filter === 'uploaded') {
                data = data.filter(r => r.results_pdf_path);
              }
              setReferredLabData(data);
            }
          } catch (error) {
            logger.error('Error fetching referred lab results:', error);
          }
        };

  useEffect(() => {
    if (currentPatient) {
        fetchLabResults();
        fetchRadResults();
        fetchReferredLabResults();
      }
      }, [currentPatient?.ris_patients_id])

  useEffect(() => {
    const risId = currentPatient?.ris_patients_id;
    if (!risId) { setPatientMrn(null); return; }
    fetch(`${BASE_URL}/patientIdentity/status?ris_patient_id=${risId}`)
      .then(r => r.json())
      .then(d => setPatientMrn(d.mrn ?? null))
      .catch(() => setPatientMrn(null));
  }, [currentPatient?.ris_patients_id]);


    useEffect(() => {
      if (currentPatient) {
        fetchReferredLabResults(referredLabFilter);
      }
    }, [referredLabFilter]); // eslint-disable-line

    useEffect(() => {
      if (selectedCategory) {
        setResults(null);
        const fetchResults = async () => {
          try {
              const response = await fetch(`${BASE_URL}/orders/results?order_type=${selectedCategory}&ris_patients_id=${currentPatient.ris_patients_id}&user=${user}&type=${currentUser.type}`);
              if (response.ok) {
              const data = await response.json();
              // logger.log("Results: " + JSON.stringify(data));
              setResults(data);
              }
          } catch (error) {
              logger.error("Error fetching radiology orders", error);
          }
        }
        fetchResults()
      }
    },[selectedCategory])


  // const handleFileUpload = async (event, order_id, order_type) => {
  //   const file = event.target.files[0];
  //   if (file) {
  //     try {
  //         await uploadToBucket(file, currentPatient.ris_patients_id, order_id, order_type);
  //         alert("PDF uploaded successfully!");
  //     } catch (error) {
  //         logger.error("Error uploading PDF:", error);
  //         alert("Failed to upload PDF.");
  //     }
  //     // handle the file upload here (e.g., send to server)
  //   }
  // };

  const handleFileUpload = async () => {
    // logger.log(selectedFile, selectedOrderId, selectedCategory)
  if (selectedFile && selectedOrderId && selectedCategory) {
    try {
      await uploadToBucket(
        selectedFile,
        selectedCategory,
        currentPatient.ris_patients_id,
        selectedOrderId,
        type,
        user,
        "Results/"
      );
      const response = await fetch(`${BASE_URL}/orders/resultsComplete`, {
          method: 'POST',
          headers: {
              'Content-Type': 'application/json',
          },
          body: JSON.stringify({id: selectedOrderId, condition: selectedCategory, user, type: currentUser.type, date_of_service: dosOverride || undefined}),
        });
        if (response.ok) {
        alert("PDF uploaded successfully!");
        resetUploadFields();
        if (selectedCategory == 'lab') {
          fetchLabResults();
        }
        if (selectedCategory == 'radiology') {
          fetchRadResults();
        }
      } else {
        logger.log("Error changing status to complete")
      }
    } catch (error) {
      logger.error("Error uploading PDF:", error);
      alert("Failed to upload PDF.");
    }
  } else {
    alert("Please select a file, category, and order before uploading.");
  }
};


  // const radiologyData = [
  //   { date: '2025-01-15', modality: 'X-Ray', description: 'Chest X-Ray', status: 'Completed' },
  //   { date: '2025-02-20', modality: 'MRI', description: 'Brain MRI', status: 'Pending' },
  //   { date: '2025-03-10', modality: 'CT Scan', description: 'Abdominal CT', status: 'In Progress' },
  // ];

  // const labData = [
  //   { date: '2025-01-10', type: 'Blood Test', description: 'Complete Blood Count', status: 'Completed' },
  //   { date: '2025-02-25', type: 'Urine Test', description: 'Urinalysis', status: 'In Progress' },
  //   { date: '2025-03-05', type: 'Biopsy', description: 'Skin Biopsy', status: 'Pending' },
  // ];

  const resetUploadFields = () => {
    setSelectedFile(null);
    setSelectedCategory('');
    setSelectedOrderId('');
    setDosOverride('');
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const handleViewResults = (orderData, orderType) => {
    // Only open if the order status is 'complete'
    if (orderData.status === 'complete') {
      setViewerModal({
        show: true,
        orderData: orderData,
        orderType: orderType
      });
    } else {
      alert('Results are not yet available for this order.');
    }
  };

  const closeViewer = () => {
    setViewerModal({ show: false, orderData: null, orderType: null });
  };

  // ── Report-fax status cell ──────────────────────────────────────────────────
  // Shows the auto-fax status of a completed radiology report and a Fax/Retry
  // action. The report consumer auto-faxes on receipt when the order opted in; this
  // lets staff fax manually, retry a failure, or send to a corrected number.
  const RAD_FAX_BADGE = {
    queued:        { label: 'Queued',   color: '#f59e0b' },
    sent:          { label: 'Sent',     color: '#10b981' },
    failed:        { label: 'Failed',   color: '#ef4444' },
    not_requested: { label: 'Not sent', color: '#9ca3af' },
  };

  const openRadFax = (order) => {
    setRadFaxOrder(order);
    setRadFaxNumber(order.fax_number || '');
  };

  const renderRadFaxCell = (radItem) => {
    // A report can only be faxed once it's complete and a PDF exists.
    const canFax = radItem.status === 'complete' && !!radItem.report_pdf_path;
    const status = radItem.fax_status || 'not_requested';
    const badge = RAD_FAX_BADGE[status] || RAD_FAX_BADGE.not_requested;
    // When it went out (delivered), or when we last tried (queued/failed). See describeFaxTiming.
    const timing = describeFaxTiming(radItem);

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span
            className="ris-referral-status-badge"
            style={{ background: badge.color }}
            title={status === 'failed' && radItem.fax_error ? radItem.fax_error : undefined}
          >
            {badge.label}
          </span>
          {canFax && (
            <button
              className="ris-little-blue-button"
              style={{ padding: '2px 8px', fontSize: '0.8em' }}
              onClick={() => openRadFax(radItem)}
            >
              {status === 'failed' ? 'Retry' : status === 'sent' || status === 'queued' ? 'Re-fax' : 'Fax'}
            </button>
          )}
        </div>
        {timing && (
          <span className="ris-date-secondary" title={timing.title}>{timing.text}</span>
        )}
      </div>
    );
  };

  // Submit a manual/retry fax for the selected radiology order.
  const sendRadFax = async () => {
    if (!radFaxOrder) return;
    setRadFaxSending(true);
    try {
      const response = await fetch(`${BASE_URL}/orders/radOrderFax/${radFaxOrder.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fax_number: radFaxNumber, user, type: currentUser.type }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.msg || 'Failed to send fax');
      toast.success(`Fax submitted! Job ID: ${data.faxId}`);
      // Optimistically reflect the new status + number in the table. The server just stamped
      // fax_last_attempt_at, so mirror that here; faxed_at is cleared because any earlier
      // delivery time describes the previous send, not this one.
      setRadData((prev) => prev.map((r) =>
        r.id === radFaxOrder.id
          ? {
              ...r,
              fax_status: 'queued',
              fax_number: radFaxNumber.trim() || r.fax_number,
              fax_error: null,
              fax_last_attempt_at: new Date().toISOString(),
              faxed_at: null,
            }
          : r
      ));
      setRadFaxOrder(null);
      setRadFaxNumber('');
    } catch (err) {
      toast.error(err.message || 'Failed to send fax');
    } finally {
      setRadFaxSending(false);
    }
  };

  const openReferralReport = async (referral) => {
    setReferralModal({ show: true, referral });
    setReferralPdfUrl(null);
    setReferralResultRows([{ ...EMPTY_ROW }]);
    setReferralReplacing(false);
    setReferralItems([]);
    setReferralRanges({});
    setReferralCheckedRanges({});
    setReferralModalLoading(true);

    try {
      // Fetch full referral details to get items with CPT codes
      const detailRes = await fetch(`${BASE_URL}/labs/referral/${referral.id}`);
      const detail = await detailRes.json();
      const items = detail.items || [];
      setReferralItems(items);

      // Reference ranges: coded items resolve by CPT, uncoded ones fall back to their
      // name. Names go only for items with no CPT — see the note in Labs.js; a name
      // that already has a code can alias-match a different test's range.
      const codes = [...new Set(items.map(i => i.cpt_code).filter(Boolean))];
      const names = [...new Set(items.filter(i => !i.cpt_code && i.item_name).map(i => i.item_name))];
      if (codes.length > 0 || names.length > 0) {
        const params = new URLSearchParams();
        if (codes.length) params.set('codes', codes.join(','));
        names.forEach(n => params.append('names', n));
        const rangesRes = await fetch(`${BASE_URL}/labs/reference-ranges?${params.toString()}`);
        const ranges = await rangesRes.json();
        setReferralRanges(ranges);
        // Default: all rows checked
        const checked = {};
        for (const [code, ref] of Object.entries(ranges)) {
          if (ref.rows) ref.rows.forEach((_, idx) => { checked[`${code}_${idx}`] = true; });
        }
        setReferralCheckedRanges(checked);
      }

      // If a PDF already exists, load it for preview
      if (referral.results_pdf_path) {
        setReferralPdfLoading(true);
        const urlRes = await fetch(
          `${BASE_URL}/upload/generate-signed-url?pdfName=${encodeURIComponent(referral.results_pdf_path)}`
        );
        const urlData = await urlRes.json();
        if (urlData.signedUrl) setReferralPdfUrl(urlData.signedUrl);
        setReferralPdfLoading(false);
      }
    } catch (error) {
      logger.error('Error loading referral details:', error);
    } finally {
      setReferralModalLoading(false);
    }
  };

  const toggleRangeCheck = (code, idx) => {
    const key = `${code}_${idx}`;
    setReferralCheckedRanges(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const buildSelectedRanges = () => {
    const result = [];
    const seen = new Set();
    for (const item of referralItems) {
      if (!item.cpt_code || seen.has(item.cpt_code)) continue;
      seen.add(item.cpt_code);
      const ref = referralRanges[item.cpt_code];
      if (!ref || !ref.rows) continue;
      const checkedRows = ref.rows.filter((_, idx) => referralCheckedRanges[`${item.cpt_code}_${idx}`]);
      if (checkedRows.length === 0) continue;
      result.push({
        cpt_code: item.cpt_code,
        name: ref.name,
        unit: ref.unit || null,
        rows: checkedRows,
        note: ref.note || null,
      });
    }
    return result;
  };

  const referralFilledRows = referralResultRows.filter(isCompleteRow);
  const referralHasReport  = !!referralModal.referral?.results_pdf_path;

  const generateReferralReport = async () => {
    const referral = referralModal.referral;
    if (!referral || referralFilledRows.length === 0) return;

    setReferralGenerating(true);
    try {
      const response = await fetch(`${BASE_URL}/labs/referral/${referral.id}/results`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          result_rows: referralFilledRows,
          selected_ranges: buildSelectedRanges(),
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.msg || 'Failed to generate report');

      const newPath = data.results_pdf_path;
      // The replacement supersedes the old report, and the backend clears the
      // validation that belonged to it — mirror that here so the fax button re-locks.
      setReferralModal(prev => ({
        ...prev,
        referral: { ...prev.referral, results_pdf_path: newPath, validated_at: null },
      }));
      setReferralReplacing(false);
      setReferralResultRows([{ ...EMPTY_ROW }]);

      // Load the newly generated PDF
      setReferralPdfLoading(true);
      const urlRes = await fetch(
        `${BASE_URL}/upload/generate-signed-url?pdfName=${encodeURIComponent(newPath)}`
      );
      const urlData = await urlRes.json();
      if (urlData.signedUrl) setReferralPdfUrl(urlData.signedUrl);
      setReferralPdfLoading(false);

      fetchReferredLabResults(referredLabFilter);
      toast.success('Report generated successfully!');
    } catch (err) {
      toast.error(err.message || 'Failed to generate report');
      logger.error(err);
    } finally {
      setReferralGenerating(false);
    }
  };

  const runReferralValidation = async (manual = false) => {
    const referral = referralModal.referral;
    if (!referral) return;
    setReferralValidationLoading(true);
    setReferralValidationError(null);
    setReferralValidationResult(null);
    try {
      const response = await fetch(`${BASE_URL}/labs/referral/${referral.id}/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ manual }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.msg || 'Validation failed');
      setReferralValidationResult(data.validationResult);
      // Only unlock fax if validation actually passed
      if (data.validated_at) {
        setReferralModal(prev => ({
          ...prev,
          referral: { ...prev.referral, validated_at: data.validated_at },
        }));
      }
    } catch (err) {
      logger.error('Referral validation error:', err);
      setReferralValidationError(err.message);
    } finally {
      setReferralValidationLoading(false);
    }
  };

  const handleFetchReports = async () => {
    setFetchingReports(true);
    try {
      const response = await fetch(`${BASE_URL}/hl7-inbound/poll`, { method: 'POST' });
      if (response.ok) {
        await fetchRadResults();
      } else {
        alert('Failed to fetch reports from AdvaPACS.');
      }
    } catch (error) {
      logger.error('Error fetching reports from AdvaPACS:', error);
      alert('Error connecting to server.');
    } finally {
      setFetchingReports(false);
    }
  };

  return (
    <div className="ris-results-container">
      <PageTopBar links={[{ label: 'Home', to: '/home', icon: Home }]} />
      <div className="ris-results-content">
        <div className="ris-results-page-header">
          <h1 className="ris-results-page-title">Results</h1>
        </div>

        {currentPatient && (
          <div className="ris-results-patient-banner">
            <div className="ris-results-patient-avatar">
              {currentPatient.first_name?.[0]?.toUpperCase()}{currentPatient.last_name?.[0]?.toUpperCase()}
            </div>
            <div className="ris-results-patient-info">
              <span className="ris-results-patient-name">
                {currentPatient.first_name} {currentPatient.last_name}
              </span>
              <span className="ris-results-patient-meta">
                {patientMrn ? `MRN: ${patientMrn}` : `ID: ${currentPatient.ris_patients_id}`}
                {currentPatient.dob && <> &nbsp;·&nbsp; DOB: {dateOfBirth(currentPatient.dob)}</>}
              </span>
            </div>
          </div>
        )}

        <div className="ris-results-upload-container">
        <div className="ris-file-upload">
          <h4 className="pdf-upload" style={{marginBottom:"0"}}>
              Upload Results PDF:
          </h4>
          <select
              className="category-select-upload"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
          >
              <option value="">Select Category</option>
              <option value="radiology">Radiology</option>
              <option value="lab">Lab</option>
          </select>
          <select
            className="category-select-upload"
            value={selectedOrderId}
            onChange={handleOrderSelect}
            disabled={!selectedCategory?.length}>
            <option value="">Select an order</option>
            {results?.map((order) => (
              <option key={order.id} value={order.id} className="order-selector">
                ID: {order.id} | {order.exam_ordered} scheduled {dateOfBirth(order.appointment_start || order.created_at)} at {formatTo12Hour(order.appointment_start || order.created_at)}
              </option>
            ))}
          </select>
          <label className="ris-upload-label">
            Date of Service (leave blank to use today):
          </label>
          <input
              className="category-select-upload"
              type="date"
              value={dosOverride}
              onChange={(e) => setDosOverride(e.target.value)}
          />
          <input
              className="pdf-upload"
              type="file"
              accept=".pdf"
              ref={fileInputRef}
              onChange={(e) => setSelectedFile(e.target.files[0])}
          />
          <button className="ris-little-blue-button" onClick={handleFileUpload}>Upload</button>
        </div>
      <div className="ris-table-section">
        <div className="ris-table-section-header">
          <h2>Lab</h2>
        </div>
        <table className="ris-results-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Scheduled</th>
              <th>Date of Service</th>
              <th>Specimen Type</th>
              <th>Description</th>
              <th>Status</th>
              <th>Priority</th>
              <th>Validation</th>
              <th>Report</th>
            </tr>
          </thead>
          <tbody>
            {labData.map((item, index) => (
              <tr
                key={index}
                onClick={() => handleViewResults(item, 'lab')}
                className={item.status === 'complete' ? 'ris-clickable-row' : ''}
                style={{ cursor: item.status === 'complete' ? 'pointer' : 'default' }}
                title={item.status === 'complete' ? 'Click to view results' : 'Results not available'}
              >
                <td>{item.id}</td>
                <td>
                  <span className="ris-date-primary">{formatAppointmentDate(item.lab_appointment_start)}</span>
                  <span className="ris-date-secondary">{formatAppointmentTimeRange(item.lab_appointment_start, item.lab_appointment_end)}</span>
                </td>
                <td>{item.specimen_collected_date ? dateOfBirth(item.specimen_collected_date) : '—'}</td>
                <td>{item.specimen_type}</td>
                <td>
                  {item.exam_ordered}
                  <AbnormalFlag status={item.abnormal_status} abnormal={item.abnormal} />
                </td>
                <td>{capitalize(item.status)}</td>
                <td>{capitalize(item.priority)}</td>
                <td className="ris-referral-status-cell" onClick={(e) => e.stopPropagation()}>
                  {item.validated_at ? (
                    <span className="ris-referral-status-badge" style={{ background: '#10b981' }}>Validated</span>
                  ) : (
                    <span className="ris-referral-status-badge" style={{ background: '#f59e0b' }}>Not Validated</span>
                  )}
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <button
                    className="ris-little-blue-button"
                    onClick={() => setLabResultsModal({ show: true, order: item })}
                  >
                    {item.results_pdf_path ? 'View Report' : 'Enter Results'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="ris-table-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h2>Radiology</h2>
          {/* <button
            className="ris-little-blue-button"
            onClick={handleFetchReports}
            disabled={fetchingReports}
          >
            {fetchingReports ? 'Fetching...' : 'Fetch Reports from AdvaPACS'}
          </button> */}
        </div>
        <table className="ris-results-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Scheduled</th>
              <th>Date of Service</th>
              <th>Modality</th>
              <th>Region</th>
              <th>Body Part</th>
              <th>Description</th>
              <th>Status</th>
              <th>Priority</th>
              <th>Report Fax</th>
            </tr>
          </thead>
          <tbody>
            {radData.map((radItem, index) => (
              <tr
                key={index}
                onClick={() => handleViewResults(radItem, 'radiology')}
                className={radItem.status === 'complete' ? 'ris-clickable-row' : ''}
                style={{ cursor: radItem.status === 'complete' ? 'pointer' : 'default' }}
                title={radItem.status === 'complete' ? 'Click to view results' : 'Results not available'}
              >
                <td>{radItem.id}</td>
                <td>
                  <span className="ris-date-primary">{formatAppointmentDate(radItem.rad_appointment_start)}</span>
                  <span className="ris-date-secondary">{formatAppointmentTimeRange(radItem.rad_appointment_start, radItem.rad_appointment_end)}</span>
                </td>
                <td>{radItem.performed_date ? dateOfBirth(radItem.performed_date) : '—'}</td>
                <td>{radItem.modality}</td>
                <td>{radItem.region || "N/A"}</td>
                <td>{radItem.body_part}</td>
                <td>{radItem.exam_ordered}</td>
                <td>{capitalize(radItem.status)}</td>
                <td>{capitalize(radItem.priority)}</td>
                <td className="ris-referral-status-cell" onClick={(e) => e.stopPropagation()}>
                  {renderRadFaxCell(radItem)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Referred Labs table */}
      <div className="ris-table-section">
        <div className="ris-table-section-header">
          <h2>Referred Labs</h2>
          <div className="ris-filter-toggle">
            <button
              className={`ris-filter-toggle-btn${referredLabFilter === 'uploaded' ? ' active' : ''}`}
              onClick={() => setReferredLabFilter('uploaded')}
            >
              All Uploaded
            </button>
            <button
              className={`ris-filter-toggle-btn${referredLabFilter === 'validated' ? ' active' : ''}`}
              onClick={() => setReferredLabFilter('validated')}
            >
              Validated Only
            </button>
          </div>
          {/* <select
            className="category-select-upload"
            style={{ width: 'auto', margin: 0 }}
            value={selectedReferralForPrint}
            onChange={(e) => setSelectedReferralForPrint(e.target.value)}
          >
            <option value="">Select Referral...</option>
            {referredLabData.map((r) => (
              <option key={r.id} value={r.id}>
                #{r.id} — {r.ordering_clinician}
              </option>
            ))}
          </select>
          <select
            className="category-select-upload"
            style={{ width: 'auto', margin: 0 }}
            value={selectedTemplate}
            onChange={(e) => setSelectedTemplate(e.target.value)}
          >
            <option value="">Print Lab Template...</option>
            <option value="blank">Blank Cover Sheet</option>
            <option value="cbc">CBC Results</option>
            <option value="chemistry">Chemistry Results</option>
            <option value="hormone">Hormone Results</option>
          </select>
          <button
            className="ris-little-blue-button"
            disabled={!selectedTemplate || (selectedTemplate !== 'blank' && !selectedReferralForPrint)}
            onClick={() => {
              const referral = referredLabData.find((r) => String(r.id) === String(selectedReferralForPrint));
              printLabTemplate(selectedTemplate, currentPatient, referral?.ordering_clinician || '');
              setSelectedTemplate('');
              setSelectedReferralForPrint('');
            }}
          >
            Print Template
          </button> */}
        </div>
        <table className="ris-results-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Created</th>
              <th>Diagnosis</th>
              <th>Tests Ordered</th>
              <th>Clinician</th>
              <th>Priority</th>
              <th>Validation</th>
              <th>Report</th>
            </tr>
          </thead>
          <tbody>
            {referredLabData.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', color: '#6b7280', padding: '14px' }}>
                  {referredLabFilter === 'validated'
                    ? 'No validated referred lab results on file.'
                    : 'No referred lab results with uploaded reports on file.'}
                </td>
              </tr>
            ) : referredLabData.map((r) => (
              <tr key={r.id}>
                <td>{r.id}</td>
                <td>{formatAppointmentDate(r.created_at)}</td>
                <td>{r.diagnosis}</td>
                <td>
                  {r.ordered_tests || 'N/A'}
                  <AbnormalFlag status={r.abnormal_status} abnormal={r.abnormal} />
                </td>
                <td>{r.ordering_clinician}</td>
                <td>{r.priority}</td>
                <td className="ris-referral-status-cell">
                  {r.validated_at ? (
                    <span className="ris-referral-status-badge" style={{ background: '#10b981' }}>Validated</span>
                  ) : (
                    <span className="ris-referral-status-badge" style={{ background: '#f59e0b' }}>Not Validated</span>
                  )}
                </td>
                <td>
                  <button
                    className="ris-little-blue-button"
                    onClick={() => openReferralReport(r)}
                  >
                    {r.results_pdf_path ? 'View Report' : 'Enter Results'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {viewerModal.show && (
        <PDFResultsViewer
          orderData={viewerModal.orderData}
          orderType={viewerModal.orderType}
          patientId={currentPatient.ris_patients_id}
          onClose={closeViewer}
        />
      )}

      {/* ── Regular lab order results template + AI validation ───────────── */}
      {labResultsModal.show && (
        <div className="pdfViewer-modal-overlay open">
          <div className="pdfViewer-modal-content" style={{ width: '760px', height: 'auto', maxWidth: '94vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
            <div className="pdfViewer-modal-header">
              <h3>Lab Results — {labResultsModal.order?.exam_ordered}</h3>
              <p className="pdfViewer-modal-subtitle">
                {currentPatient?.first_name} {currentPatient?.last_name} · Order #{labResultsModal.order?.id}
              </p>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', minHeight: 0 }}>
              <LabResultsWorkflow
                orderId={labResultsModal.order?.id}
                cptCode={labResultsModal.order?.cpt_code}
                examName={labResultsModal.order?.exam_ordered}
                patient={currentPatient}
                initialResultsPdfPath={labResultsModal.order?.results_pdf_path}
                initialValidatedAt={labResultsModal.order?.validated_at}
                onUpdated={fetchLabResults}
              />
            </div>

            <div className="pdfViewer-modal-footer">
              <button
                className="pdfViewer-close-button"
                onClick={() => { setLabResultsModal({ show: false, order: null }); fetchLabResults(); }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Referred lab report viewer modal ─────────────────────────── */}
      {referralModal.show && (
        <div className="pdfViewer-modal-overlay open">
          <div className="pdfViewer-modal-content">
            <div className="pdfViewer-modal-header">
              <h3>Referred Lab Report #{referralModal.referral?.id} — {referralModal.referral?.first_name} {referralModal.referral?.last_name}</h3>
              <p className="pdfViewer-modal-subtitle">{referralModal.referral?.diagnosis}</p>
            </div>

            {/* Scrollable body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '16px', minHeight: 0 }}>
              {referralModalLoading ? (
                <p style={{ textAlign: 'center', color: '#6b7280', padding: '20px' }}>Loading…</p>
              ) : (
                <>
                  {/* PDF preview */}
                  {(referralPdfLoading || referralPdfUrl) && (
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden', height: '420px', flexShrink: 0 }}>
                      {referralPdfLoading ? (
                        <p style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>Loading report…</p>
                      ) : (
                        <iframe
                          src={referralPdfUrl}
                          title="Referred Lab Report"
                          width="100%"
                          height="100%"
                          style={{ border: 'none', display: 'block' }}
                        />
                      )}
                    </div>
                  )}

                  {/* Results entry — shown until a report exists, so the "Enter
                      Results" button on the worklist actually leads somewhere, and
                      again on demand when an existing report is being replaced. */}
                  {(!referralHasReport || referralReplacing) && (
                    <div style={{ padding: '16px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px' }}>
                      <h4 style={{ marginTop: 0, marginBottom: '12px', color: '#166534' }}>
                        {referralHasReport ? 'Replace Results — one analyte per row' : 'Enter Results — one analyte per row'}
                      </h4>
                      {referralHasReport && (
                        <div style={{ marginBottom: '12px', padding: '10px 12px', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '6px', fontSize: '13px', color: '#92400e' }}>
                          Generating a new report will supersede the report currently on file and
                          clear its validation.
                        </div>
                      )}
                      <ResultRowsEditor
                        rows={referralResultRows}
                        onChange={setReferralResultRows}
                        disabled={referralGenerating}
                      />

                      {/* Reference range checkboxes — same control as the Labs page, so a
                          report carries the same ranges section whichever page keyed it. */}
                      {Object.keys(referralRanges).length > 0 && (
                        <div style={{ marginTop: '14px' }}>
                          <p style={{ fontWeight: 600, fontSize: '13px', color: '#166534', marginBottom: '8px' }}>
                            Reference Ranges — check applicable ranges to include in report:
                          </p>
                          {referralItems
                            .filter((item, idx, arr) => item.cpt_code && arr.findIndex(i => i.cpt_code === item.cpt_code) === idx)
                            .map(item => {
                              const ref = referralRanges[item.cpt_code];
                              if (!ref || !ref.rows) return null;
                              return (
                                <div key={item.cpt_code} style={{ marginBottom: '10px', padding: '10px 12px', background: '#fff', border: '1px solid #86efac', borderRadius: '6px' }}>
                                  <p style={{ fontWeight: 600, fontSize: '12px', color: '#0e8a8f', marginBottom: '6px' }}>
                                    {ref.name}{ref.unit ? ` (${ref.unit})` : ''}
                                  </p>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    {ref.rows.map(([label, range], idx) => (
                                      <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', cursor: 'pointer', color: '#374151' }}>
                                        <input
                                          type="checkbox"
                                          checked={!!referralCheckedRanges[`${item.cpt_code}_${idx}`]}
                                          onChange={() => toggleRangeCheck(item.cpt_code, idx)}
                                          disabled={referralGenerating}
                                          style={{ width: '14px', height: '14px', cursor: 'pointer' }}
                                        />
                                        <span style={{ flex: 1 }}>{label}</span>
                                        <span style={{ color: '#6b7280', minWidth: '120px', textAlign: 'right' }}>{range}</span>
                                      </label>
                                    ))}
                                  </div>
                                  {ref.note && <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>* {ref.note}</p>}
                                </div>
                              );
                            })}
                        </div>
                      )}

                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '12px' }}>
                        <button
                          className="ris-bigger-blue-button"
                          onClick={generateReferralReport}
                          disabled={referralFilledRows.length === 0 || referralGenerating}
                        >
                          {referralGenerating
                            ? 'Generating Report…'
                            : referralHasReport ? 'Generate Replacement Report' : 'Generate Report'}
                        </button>
                        {referralHasReport && (
                          <button
                            className="pdfViewer-close-button"
                            disabled={referralGenerating}
                            onClick={() => { setReferralReplacing(false); setReferralResultRows([{ ...EMPTY_ROW }]); }}
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="pdfViewer-modal-footer" style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                className="ris-modal-btn-replace"
                disabled={!referralHasReport || referralReplacing || referralGenerating}
                title={referralHasReport ? 'Enter new results to supersede this report' : 'No report on file yet'}
                onClick={() => { setReferralResultRows([{ ...EMPTY_ROW }]); setReferralReplacing(true); }}
              >
                Replace Results
              </button>
              <button
                className="ris-modal-btn-validate"
                onClick={() => { setReferralValidationModal(true); runReferralValidation(false); }}
                disabled={!referralModal.referral?.results_pdf_path}
              >
                Validate
              </button>
              <button
                className="ris-modal-btn-fax"
                disabled={!referralModal.referral?.results_pdf_path}
                onClick={() => {
                  if (!referralModal.referral?.validated_at) {
                    setReferralUnvalidatedFaxConfirm(true);
                  } else {
                    setReferralFaxModal(true);
                  }
                }}
              >
                Send as Fax
              </button>
              <button
                className="pdfViewer-close-button"
                onClick={() => { setReferralModal({ show: false, referral: null }); setReferralPdfUrl(null); setReferralResultRows([{ ...EMPTY_ROW }]); setReferralReplacing(false); }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Referred lab validation modal ─────────────────────────────── */}
      {referralValidationModal && (
        <div className="pdfViewer-modal-overlay open" style={{ zIndex: 1200 }}>
          <div
            className="pdfViewer-modal-content"
            style={{ width: '520px', height: 'auto', maxWidth: '92vw', padding: '0' }}
          >
            <div className="pdfViewer-modal-header">
              <h3>AI Validation</h3>
              <p className="pdfViewer-modal-subtitle">
                Referral #{referralModal.referral?.id} — {referralModal.referral?.first_name} {referralModal.referral?.last_name}
              </p>
            </div>
            <div style={{ padding: '20px 24px' }}>
              {referralValidationLoading && (
                <div className="ris-validation-loading">
                  <GearsSpinner alt="Validating…" />
                  <p>Analyzing document…</p>
                </div>
              )}
              {!referralValidationLoading && referralValidationError && (
                <>
                  <div className="ris-validation-error">{referralValidationError}</div>
                  <button className="ris-manual-validate-btn" onClick={() => runReferralValidation(true)}>
                    Unable to reach AI — Mark as Manually Validated
                  </button>
                </>
              )}
              {!referralValidationLoading && referralValidationResult && (
                <div className="ris-validation-cards">
                  <div className="ris-validation-card">
                    <div className="ris-validation-card-header">
                      <span className="ris-validation-card-title">Name Check</span>
                      <span className={`ris-validation-badge ${referralValidationResult.name_check?.pass ? 'ris-validation-badge-pass' : 'ris-validation-badge-fail'}`}>
                        {referralValidationResult.name_check?.pass ? 'Pass' : 'Fail'}
                      </span>
                    </div>
                    <div className="ris-validation-card-body">
                      {referralValidationResult.name_check?.found_name && (
                        <div className="ris-validation-row"><span className="ris-validation-label">Name found:</span><span className="ris-validation-value">{referralValidationResult.name_check.found_name}</span></div>
                      )}
                      {referralValidationResult.name_check?.found_dob && (
                        <div className="ris-validation-row"><span className="ris-validation-label">DOB found:</span><span className="ris-validation-value">{referralValidationResult.name_check.found_dob}</span></div>
                      )}
                      {referralValidationResult.name_check?.found_age && (
                        <div className="ris-validation-row"><span className="ris-validation-label">Age found:</span><span className="ris-validation-value">{referralValidationResult.name_check.found_age}</span></div>
                      )}
                      {referralValidationResult.name_check?.details && (
                        <p className="ris-validation-details">{referralValidationResult.name_check.details}</p>
                      )}
                    </div>
                  </div>
                  <div className="ris-validation-card">
                    <div className="ris-validation-card-header">
                      <span className="ris-validation-card-title">Order Check</span>
                      <span className={`ris-validation-badge ${referralValidationResult.order_check?.pass ? 'ris-validation-badge-pass' : 'ris-validation-badge-fail'}`}>
                        {referralValidationResult.order_check?.pass ? 'Pass' : 'Fail'}
                      </span>
                    </div>
                    <div className="ris-validation-card-body">
                      {referralValidationResult.order_check?.found_tests?.length > 0 && (
                        <div className="ris-validation-row"><span className="ris-validation-label">Tests found:</span><span className="ris-validation-value">{referralValidationResult.order_check.found_tests.join(', ')}</span></div>
                      )}
                      {referralValidationResult.order_check?.details && (
                        <p className="ris-validation-details">{referralValidationResult.order_check.details}</p>
                      )}
                    </div>
                  </div>
                  {referralValidationResult.manual && (
                    <p style={{ textAlign: 'center', margin: 0 }}>
                      <span className="ris-validation-badge ris-validation-badge-manual">Manually Validated</span>
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="pdfViewer-modal-footer">
              <button
                className="pdfViewer-close-button"
                onClick={() => { setReferralValidationModal(false); setReferralValidationResult(null); setReferralValidationError(null); }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Unvalidated fax confirmation modal ───────────────────────── */}
      {referralUnvalidatedFaxConfirm && (
        <div className="pdfViewer-modal-overlay open" style={{ zIndex: 1300 }}>
          <div className="pdfViewer-modal-content" style={{ width: '420px', height: 'auto', maxWidth: '90vw', padding: '0' }}>
            <div className="pdfViewer-modal-header">
              <h3>Unvalidated Report</h3>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <p style={{ fontSize: '14px', color: '#374151', margin: 0 }}>
                This report has not been validated. Are you sure you want to send it as a fax?
              </p>
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', padding: '0 24px 20px' }}>
              <button className="pdfViewer-close-button" onClick={() => setReferralUnvalidatedFaxConfirm(false)}>Cancel</button>
              <button
                className="ris-modal-btn-fax"
                onClick={() => { setReferralUnvalidatedFaxConfirm(false); setReferralFaxModal(true); }}
              >
                Send Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Referred lab fax modal ────────────────────────────────────── */}
      {referralFaxModal && (
        <div className="pdfViewer-modal-overlay open" style={{ zIndex: 1100 }}>
          <div className="pdfViewer-modal-content" style={{ width: '420px', height: 'auto', maxWidth: '90vw', padding: '24px' }}>
            <div className="pdfViewer-modal-header">
              <h3>Send Results by Fax</h3>
              <p className="pdfViewer-modal-subtitle">
                Enter the recipient fax number for {referralModal.referral?.first_name} {referralModal.referral?.last_name}'s lab results.
              </p>
            </div>
            <div style={{ padding: '20px 0 8px' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', fontSize: '14px', color: '#374151' }}>
                Fax Number
              </label>
              <InputMask
                mask="(XXX) XXX-XXXX"
                replacement={{ X: /\d/ }}
                type="tel"
                placeholder="(555) 555-5555"
                value={referralFaxNumber}
                onChange={e => setReferralFaxNumber(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '15px', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', paddingTop: '12px' }}>
              <button className="pdfViewer-close-button" onClick={() => { setReferralFaxModal(false); setReferralFaxNumber(''); }}>Cancel</button>
              <button
                className="ris-modal-btn-fax"
                disabled={!referralFaxNumber.trim() || referralFaxSending}
                onClick={async () => {
                  setReferralFaxSending(true);
                  try {
                    const response = await fetch(`${BASE_URL}/labs/referral/${referralModal.referral?.id}/fax`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ fax_number: referralFaxNumber }),
                    });
                    const data = await response.json();
                    if (!response.ok) throw new Error(data.msg || 'Failed to send fax');
                    toast.success(`Fax sent successfully! Job ID: ${data.faxId}`);
                    setReferralFaxModal(false);
                    setReferralFaxNumber('');
                  } catch (err) {
                    toast.error(err.message || 'Failed to send fax');
                  } finally {
                    setReferralFaxSending(false);
                  }
                }}
              >
                {referralFaxSending ? 'Sending…' : 'Send Fax'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Radiology report fax modal (manual send / retry) ──────────── */}
      {radFaxOrder && (
        <div className="pdfViewer-modal-overlay open" style={{ zIndex: 1100 }}>
          <div className="pdfViewer-modal-content" style={{ width: '420px', height: 'auto', maxWidth: '90vw', padding: '24px' }}>
            <div className="pdfViewer-modal-header">
              <h3>Fax Radiology Report</h3>
              <p className="pdfViewer-modal-subtitle">
                Enter the recipient fax number for order #{radFaxOrder.id} ({radFaxOrder.exam_ordered || radFaxOrder.modality}).
              </p>
            </div>
            <div style={{ padding: '20px 0 8px' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', fontSize: '14px', color: '#374151' }}>
                Fax Number
              </label>
              <InputMask
                mask="(XXX) XXX-XXXX"
                replacement={{ X: /\d/ }}
                type="tel"
                placeholder="(555) 555-5555"
                value={radFaxNumber}
                onChange={e => setRadFaxNumber(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '15px', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', paddingTop: '12px' }}>
              <button className="pdfViewer-close-button" onClick={() => { setRadFaxOrder(null); setRadFaxNumber(''); }}>Cancel</button>
              <button
                className="ris-modal-btn-fax"
                disabled={!radFaxNumber.trim() || radFaxSending}
                onClick={sendRadFax}
              >
                {radFaxSending ? 'Sending…' : 'Send Fax'}
              </button>
            </div>
          </div>
        </div>
      )}
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default Results;